<template>
  <div>
    <ListView
      :title="displayTitle"
      :enable-create="true"
      :data="data"
      :page.sync="page"
      :header="headers"
      :search.sync="search"
      @reload="reload"
      @create="openHolidayDialog()"
      @navDetails="() => {}"
      :enableCreate="false"
    >
      <template #btn>
        <div>
          <div class="d-flex">
            <v-btn v-if="!doctorId" color="primary" @click="openImportDialog">
              Import HK Public Holidays
            </v-btn>

            <v-spacer />

            <v-btn color="primary" @click="openHolidayDialog()">
              <v-icon left>mdi-plus</v-icon>
              Add Holiday
            </v-btn>
          </div>
          <div class="text-center text-caption font-weight-bold" style="color: red">
            * After added , all bookings will be cancelled for the holiday period*
          </div>
        </div>
      </template>

      <template v-slot:item.name_en="{ item }">
        {{ item.name | parseAndLocalize('en') }}
      </template>
      <template v-slot:item.name_zh_hk="{ item }">
        {{ item.name | parseAndLocalize('zh-hk') }}
      </template>

      <template v-slot:item.time="{ item }">
        <div v-if="item.time">
          {{ formatDateRange(item.time) }}
        </div>
        <span v-else>-</span>
      </template>

      <template v-slot:item.duration="{ item }">
        {{ getDuration(item.time) }}
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip :color="getStatusColor(item.time)" small dark>
          {{ getStatusText(item.time) }}
        </v-chip>
      </template>

      <template v-slot:item.createdAt="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>

      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" @click.stop="openHolidayDialog(item)"> mdi-pencil </v-icon>
        <v-icon small color="error" @click.stop="deleteHoliday(item)"> mdi-delete </v-icon>
      </template>
    </ListView>

    <!-- Import Holidays Dialog (only for clinic holidays) -->
    <v-dialog v-if="!doctorId" v-model="importDialog" max-width="400px">
      <v-card>
        <v-card-title class="primaryDialogTitle">
          <span class="headline">Import HK Public Holidays</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <p class="mb-3">Select a year to import Hong Kong public holidays:</p>
                <v-radio-group v-model="selectedYear" column>
                  <v-radio
                    v-for="year in availableYears"
                    :key="year"
                    :label="`${year} ${year === currentYear ? '(Current Year)' : '(Next Year)'}`"
                    :value="year"
                  ></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeImportDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="importLoading"
            :disabled="!selectedYear"
            @click="importHolidays"
          >
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Holiday Edit/Create Dialog -->
    <v-dialog v-model="holidayDialog" max-width="600px" persistent>
      <HolidayCard
        v-if="holidayDialog"
        :holiday-id="selectedHoliday && selectedHoliday._id"
        :holiday-data="selectedHoliday"
        :clinic-id="clinicId"
        :doctor-id="doctorId"
        @close="closeHolidayDialog"
        @saved="onHolidaySaved"
      />
    </v-dialog>
  </div>
</template>

<script>
import listViewPageMixin from '@/services/listViewPageMixin';
import { mapActions } from 'vuex';
import moment from 'moment';
import api from '@/api';
import HolidayCard from './HolidayCard';
import { RESTFUL, holidayType, holidayReason } from '@/data/constants';

export default {
  name: 'ClinicHolidays',
  mixins: [listViewPageMixin],
  components: {
    HolidayCard,
  },
  props: {
    clinicId: {
      type: String,
      required: true,
    },
    doctorId: {
      type: String,
      default: null,
    },
    doctorName: {
      type: String,
      default: '',
    },
  },
  data() {
    const currentYear = moment().year();
    const nextYear = currentYear + 1;

    return {
      holidayDialog: false,
      selectedHoliday: null,
      importDialog: false,
      importLoading: false,
      selectedYear: currentYear,
      currentYear,
      nextYear,
      availableYears: [currentYear, nextYear],

      populate: null,
      sort: { 'time.start': -1 },
    };
  },
  computed: {
    restfulURL() {
      return RESTFUL.holidays.list;
    },
    filter() {
      const filter = { clinic: this.clinicId, reason: holidayReason.scheduled };
      if (this.doctorId) {
        filter.doctor = this.doctorId;
      }
      return filter;
    },
    title() {
      if (this.doctorId && this.doctorName) {
        return `${this.doctorName} - Holidays`;
      }
      return 'Holidays';
    },
    displayTitle() {
      if (this.doctorId && this.doctorName) {
        return ''; // No title when used for doctor (in dialog)
      }
      return 'Holidays'; // Show title when used for clinic
    },
    headers() {
      return [
        ...(this.doctorId ? [{ text: 'Created', value: 'createdAt' }] : []),
        { text: 'Date Range', value: 'time' },
        { text: 'Name (EN)', value: 'name_en' },
        { text: 'Name (ZH-HK)', value: 'name_zh_hk' },
        // { text: 'Duration', value: 'duration', sortable: false },
        // { text: 'Status', value: 'status', sortable: false },
        // { text: 'Created', value: 'created_at' },
        { text: 'Actions', value: 'actions', sortable: false },
      ];
    },
  },
  methods: {
    ...mapActions('alert', ['updateAlertMessage']),

    formatDate(date) {
      return date ? moment(date).format('DD-MMM-YYYY') : '-';
    },

    formatDateRange(time) {
      if (!time || !time.start || !time.end) return '-';
      const start = moment(time.start).format('DD-MMM-YYYY');
      const end = moment(time.end).format('DD-MMM-YYYY');
      if (start === end) {
        return start;
      }
      return `${start} - ${end}`;
    },

    getDuration(time) {
      if (!time || !time.start || !time.end) return '-';
      const start = moment(time.start);
      const end = moment(time.end);
      const days = end.diff(start, 'days') + 1;
      return days === 1 ? '1 day' : `${days} days`;
    },

    getStatusColor(time) {
      if (!time || !time.start || !time.end) return 'grey';
      const now = moment();
      const start = moment(time.start);
      const end = moment(time.end);

      if (now.isBefore(start)) {
        return 'info'; // Upcoming
      } else if (now.isAfter(end)) {
        return 'grey'; // Past
      } else {
        return 'warning'; // Ongoing
      }
    },

    getStatusText(time) {
      if (!time || !time.start || !time.end) return 'Unknown';
      const now = moment();
      const start = moment(time.start);
      const end = moment(time.end);

      if (now.isBefore(start)) {
        return 'Upcoming';
      } else if (now.isAfter(end)) {
        return 'Past';
      } else {
        return 'Ongoing';
      }
    },

    openHolidayDialog(holiday = null) {
      this.selectedHoliday = holiday;
      this.holidayDialog = true;
    },

    closeHolidayDialog() {
      this.holidayDialog = false;
      this.selectedHoliday = null;
    },

    async onHolidaySaved(holiday) {
      await this.reload();
      this.closeHolidayDialog();
    },

    async deleteHoliday(holiday) {
      const holidayName = this.$options.filters.parseAndLocalize(holiday.name, 'en');

      this.$root.$confirm
        .open('Confirm Delete', `Are you sure you want to delete the holiday "${holidayName}"?`, {
          color: 'red',
        })
        .then(async confirm => {
          if (confirm) {
            try {
              await api.delete(`${RESTFUL.holidays.delete}/${holiday._id}`);
              await this.updateAlertMessage({
                msg: 'Holiday deleted successfully',
                type: 'success',
                color: 'success',
              });
              await this.reload();
            } catch (error) {
              console.error('Error deleting holiday:', error);
              await this.updateAlertMessage({
                msg: 'Failed to delete holiday',
                type: 'error',
              });
            }
          }
        });
    },

    openImportDialog() {
      this.selectedYear = this.currentYear;
      this.importDialog = true;
    },

    closeImportDialog() {
      this.importDialog = false;
      this.selectedYear = this.currentYear;
    },

    async importHolidays() {
      if (!this.selectedYear) {
        await this.updateAlertMessage({
          msg: 'Please select a year',
          type: 'warning',
        });
        return;
      }

      try {
        this.importLoading = true;

        const payload = {
          clinic: this.clinicId,
          year: this.selectedYear,
        };

        if (this.doctorId) {
          payload.doctor = this.doctorId;
        }

        await api.post(RESTFUL.holidays.importHK, payload);

        await this.updateAlertMessage({
          msg: `Successfully imported HK public holidays for ${this.selectedYear}`,
          type: 'success',
          color: 'success',
        });

        this.closeImportDialog();
        await this.reload();
      } catch (error) {
        console.error('Error importing holidays:', error);
        const errorMsg = error.response?.data?.message || 'Failed to import holidays';
        await this.updateAlertMessage({
          msg: errorMsg,
          type: 'error',
        });
      } finally {
        this.importLoading = false;
      }
    },
  },
};
</script>

<style scoped></style>
